/* eslint-disable react/jsx-max-depth */
import {
  Bill,
  BillAction,
  BillSponsor,
  Hearing,
} from '@enview/interface/types/bills/Bill';
import { BillSummary } from '@enview/interface/types/bills/Bill';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import DataTable, { IDataTableColumn } from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  getOrganizationUser,
  getOrgForTeamMode,
  getTeamMode,
  updateOrganizationUser,
} from '../../dux';
import { convertIdToAbbreviatedTitle, getBillAlias } from '../../helpers/BillHelper';
import { SortBy, SortOrder } from '../../models/SortOrder';
import BillTagButton from '../Shared/BillTagging/BillTagButton';
import BillTrackingButton from '../Shared/BillTracking/BillTrackingButton';
import BillBulkActionsDropdown from '../Shared/DropDowns/BillBulkActionsDropdown';
import BillViewDropdown from '../Shared/DropDowns/BillViewDropdown';
import BillStatusBadge from './BillStatusBadge';
import NoBillResults from './NoBillResults';
import BillPosition from '../Shared/BillPosition/BillPosition';
import Info from '../../components/svg/InfoCircleIcon';
import {
  buildColumnOptions,
  democratPartyIdentifier,
  partyString,
  republicanPartyIdentifier,
  splitByDate,
} from '../../utils';
import { IllinoisKeyActions } from '@enview/interface/utils/Language';
import { getMostRecentAction, formatDate } from '../../helpers/BillHelper';
import FeedbackPrompt from '../Shared/Prompts/FeedbackPrompt';
import { FeedbackType } from '@enview/interface/types/actions/FeedbackData';
import { useTranslation } from 'react-i18next';
import useSearchId from '../../hooks/useSearchId';
import * as BillAnalytics from '../../analytics/BillAnalytics';
import ColumnSelectDropdown from '../Shared/DropDowns/ColumnSelectDropdown';
import { CustomFieldsAPI } from '../../api';
import BillCustomFieldValue from '../Shared/BillCustomField/BillCustomFieldValue';
import {
  DefaultBillTableColumns,
  REQUIRED_COLUMNS,
} from '@enview/interface/types/Bill';
import { State } from '../../dux/@types';
import { skipToken } from '@reduxjs/toolkit/query';

export interface SortableDataTableColumn extends IDataTableColumn {
  sortKey?: SortBy;
}

export type ColumnWithId = SortableDataTableColumn & { columnId: string };

type TabularBillSearchResultProps = {
  params: any;
  searching: boolean;
  setSearching: (arg: boolean) => void;
  query: any;
  setQuery: (arg: any) => void;
  results:
    | {
        data: BillSummary[];
        count: number;
      }
    | undefined;
  isSuccess: boolean;
};

// TODO: Refactor to use modular BillTable component
const TabularBillSearchResults = (
  props: TabularBillSearchResultProps,
): ReactElement => {
  const { params, searching, setSearching, query, setQuery, results, isSuccess } =
    props;
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState([] as Bill[]);
  const [loadingPage, setLoadingPage] = useState(true);

  const [tooltipAnchor, setTooltipAnchor] = useState('');
  const [tipContent, setTipContent] = useState<ReactElement<any>>(<></>);
  const [showTooltip, setShowTooltip] = useState(false);
  const orgUser = useSelector((state: State) => state.account.organizationUser);

  const initialColumns =
    orgUser?.settings.billViewPreference?.views.billSearchView?.columns;
  const defaultColumns = Object.values(DefaultBillTableColumns);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    initialColumns ?? defaultColumns,
  );
  const organization = useSelector(getOrgForTeamMode);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!orgUser) return;
    dispatch(
      updateOrganizationUser(
        {
          ...orgUser,
          firstName: orgUser.firstName,
          lastName: orgUser.lastName,
          email: orgUser.email,
          settings: {
            ...orgUser.settings,
            billViewPreference: {
              views: {
                billSearchView: {
                  columns: selectedColumns,
                  updatedAt: Date.now().toLocaleString(),
                },
              },
            },
          },
        },
        orgUser,
      ),
    );
    dispatch(getOrganizationUser());
  }, [selectedColumns]);

  const orgId = organization?.id;

  const { data: customFields = [] } =
    CustomFieldsAPI.endpoints.getOrganizationCustomFields.useQuery(
      orgId ? orgId : skipToken,
    );

  const columnOptions = useMemo(() => buildColumnOptions(customFields), [customFields]);

  const handleColumnChange = (values: string[]) => {
    const withRequired = Array.from(new Set([...values, ...REQUIRED_COLUMNS]));
    setSelectedColumns(withRequired);
  };

  const { searchId } = useSearchId();

  const [sort, setSort] = useState<SortOrder>(
    new SortOrder(SortBy.RECENT_ACTIVITY, false),
  );

  const handleSortChange = useCallback(
    (column: SortableDataTableColumn, sortDirection: 'desc' | 'asc') => {
      const sortColumn = column.sortKey ?? SortBy.RECENT_ACTIVITY;
      const sortOrder = new SortOrder(sortColumn, sortDirection === 'desc');
      if (isEqual(sort, sortOrder)) return;
      setLoadingPage(true);
      setSort(sortOrder);
      setQuery({
        order: sortOrder.getQueryParam(),
      });
    },
    [sort, setQuery],
  );

  useEffect(() => {
    if (query.pageSize) {
      return;
    }
    setQuery({ pageSize: 10 });
  }, [setQuery]);

  const teamModeId = useSelector(getTeamMode);

  const handleSearchFeedback = (isPositve: boolean): void => {
    BillAnalytics.trackAISearchFeedback(
      searchId,
      isPositve,
      query.page,
      query.pageSize,
      results?.count,
    );
  };

  const isIllinoisSearch: boolean = useMemo(
    () =>
      params?.criteria?.jurisdictions?.length > 0 &&
      params.criteria?.jurisdictions?.every((jur: string) => jur === 'il'),
    [params],
  );

  const handleSelectedRowsChange = useCallback(
    (state: { selectedRows: Bill[] }) => {
      setSelectedRows(state.selectedRows);
    },
    [setSelectedRows],
  );

  const handleSelectableRowsSelected = useCallback(
    (row: Bill) => {
      return selectedRows.some((selectedRow) => selectedRow.id === row.id);
    },
    [setSelectedRows],
  );

  const handlePageChange = useCallback((pageParam: number) => {
    setLoadingPage(true);
    setQuery({ page: pageParam });
  }, []);

  const handlePageSizeChange = useCallback((pageSizeParam: number) => {
    setLoadingPage(true);
    setQuery({ pageSize: pageSizeParam });
  }, []);

  useEffect(() => {
    setSearching(false);
    if (results) setLoadingPage(false);
    setSelectedRows((prevSelectedRows) => {
      const updatedRowSelections =
        results?.data?.filter((result) => {
          return prevSelectedRows.some((selectedRow) => {
            return result.id === selectedRow.id;
          });
        }) ?? [];
      return updatedRowSelections;
    });
  }, [results]);

  const getMostRecentKeyAction = (bill: BillSummary) => {
    const sortedActions = bill.actions ? [...bill.actions].sort(sortActions) : [];
    // We're looking for an action on this bill whose description starts with one of the
    // strings in the IllinoisKeyActions list
    const latestKeyAction = sortedActions.find((action) =>
      IllinoisKeyActions.some((keyAction) => action.description.startsWith(keyAction)),
    );
    if (!latestKeyAction) return <></>;
    const actionDate = formatDate(latestKeyAction.actionDate);

    let chamber;
    if (latestKeyAction.chamber === 'upper') {
      chamber = 'Senate';
    } else if (latestKeyAction.chamber === 'lower') {
      chamber = 'House';
    }

    const keyActionId = 'latest-key-action' + bill.sourceBillId;

    const latestKeyActionContent = chamber
      ? `${actionDate} - ${chamber} - ${latestKeyAction.description}`
      : `${actionDate} - ${latestKeyAction.description}`;

    const handleKeyActionFocus = () => {
      setTipContent(<p>{latestKeyActionContent}</p>);
      setTooltipAnchor(keyActionId);
      setShowTooltip(true);
    };

    return (
      <p
        className="latest-key-action"
        id={keyActionId}
        onClick={handleKeyActionFocus}
        onMouseEnter={handleKeyActionFocus}
      >
        {latestKeyActionContent}
      </p>
    );
  };

  const sortActions = (a: BillAction, b: BillAction): number => {
    const dateDiff = moment.utc(b.actionDate).diff(moment.utc(a.actionDate));
    if (dateDiff === 0) {
      return b.order - a.order;
    }
    return dateDiff;
  };

  const showTableResults = !searching && isSuccess && results && results.count > 0;

  const handleTooltipContentClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const tooltipContent = (
    primarySponsors: BillSponsor[],
    coSponsors: BillSponsor[],
  ) => {
    return (
      <div className="sponsors-tooltip" onClick={handleTooltipContentClick}>
        <span className="tooltip-header">Primary sponsors:</span>
        <ul>
          {primarySponsors.map((sponsor, index) => (
            <li key={`primary-${index}`}>
              {sponsor.person?.id ? (
                <Link to={`/person/${sponsor.person?.id}`}>
                  {`${sponsor.name}${partyString(sponsor)}`}
                </Link>
              ) : (
                `${sponsor.name}${partyString(sponsor)}`
              )}
            </li>
          ))}
        </ul>
        {!!coSponsors.length && (
          <>
            <span className="tooltip-header">Co-sponsors:</span>
            <ul>
              {coSponsors.map((sponsor, index) => (
                <li key={`co-${index}`}>
                  {sponsor.person?.id ? (
                    <Link to={`/person/${sponsor.person?.id}`}>
                      {`${sponsor.name}${partyString(sponsor)}`}
                    </Link>
                  ) : (
                    `${sponsor.name}${partyString(sponsor)}`
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderBillSponsors = (bill: Bill) => {
    const { sponsors } = bill;
    if (!sponsors) return undefined;

    const currentSponsors = sponsors.filter((s) => !s.isRemoved);
    const primarySponsors = currentSponsors.filter((s) => s.isPrimary);
    const coSponsors = currentSponsors.filter((s) => !s.isPrimary && !s.isRemoved);

    if (!primarySponsors.length) return undefined;

    const tooltipId = `table-tooltip-${bill.id}`;

    const handleSponsorFocus = () => {
      setTipContent(tooltipContent(primarySponsors, coSponsors));
      setTooltipAnchor(tooltipId);
      setShowTooltip(true);
    };

    return (
      <p
        className="bill-sponsor-table-cell"
        id={tooltipId}
        key={bill.id}
        onClick={handleSponsorFocus}
        onMouseEnter={handleSponsorFocus}
      >
        <span>{primarySponsors[0].name + partyString(primarySponsors[0])}</span>
        <Info className="ml-1" color="#4a4a4a" size={16} />
      </p>
    );
  };

  const renderUpcomingHearings = (bill: BillSummary) => {
    const { hearings } = bill;
    if (!hearings || hearings.length === 0) return;

    const { upcomingEvents, pastEvents } = splitByDate(hearings);
    let displayedEvent: Hearing;

    if (upcomingEvents.length > 0) {
      displayedEvent = upcomingEvents.reduce((nearest, curr) =>
        new Date(curr.start).getTime() < new Date(nearest.start).getTime()
          ? curr
          : nearest,
      );
    } else {
      displayedEvent = pastEvents.reduce((latest, curr) =>
        new Date(curr.start).getTime() > new Date(latest.start).getTime()
          ? curr
          : latest,
      );
    }

    const hearingDate = formatDate(displayedEvent.start, 'short');
    const upcomingHearingsId = 'upcoming-hearing' + bill.sourceBillId;
    const upcomingEventContent = `${hearingDate} - ${displayedEvent.eventData.name}`;

    const handleUpcomingHearingFocus = () => {
      setTipContent(<p>{upcomingEventContent}</p>);
      setTooltipAnchor(upcomingHearingsId);
      setShowTooltip(true);
    };

    return (
      <p
        id={upcomingHearingsId}
        onClick={handleUpcomingHearingFocus}
        onMouseEnter={handleUpcomingHearingFocus}
      >
        {upcomingEventContent}
      </p>
    );
  };

  const columns: SortableDataTableColumn[] = useMemo(() => {
    const baseColumns: ColumnWithId[] = [
      {
        columnId: DefaultBillTableColumns.TRACKING,
        name: 'Tracking',
        sortable: false,
        ignoreRowClick: true,
        minWidth: '110px',
        style: {
          paddingLeft: '0px',
        },
        cell: (bill: Bill) => (
          <BillTrackingButton bill={bill} hasStatusIndicator isCompactMode />
        ),
      },
      {
        columnId: DefaultBillTableColumns.TAGS,
        name: 'Tags',
        selector: 'tags',
        ignoreRowClick: true,
        right: true,
        width: '50px',
        cell: (bill: Bill) => {
          return useMemo(
            () => <BillTagButton bill={bill} hasStatusIndicator />,
            [bill.tags],
          );
        },
      },
      {
        columnId: DefaultBillTableColumns.POSITION,
        name: 'Position',
        selector: 'position',
        ignoreRowClick: true,
        right: true,
        width: '80px',
        cell: (bill: Bill) => <BillPosition bill={bill} />,
      },
      {
        columnId: DefaultBillTableColumns.BILL_ID,
        name: 'Bill ID',
        selector: 'id',
        sortable: true,
        maxWidth: '200px',
        sortKey: SortBy.ALPHANUMERICAL,
        style: {
          padding: '0px 12px',
        },
        cell(bill: Bill) {
          const title = convertIdToAbbreviatedTitle(bill.id);
          const billAlias = getBillAlias(bill.userAliases, teamModeId);
          return (
            <Link
              className="bill-link"
              to={`/legislative-tracking/bill/details/${bill.id}`}
            >
              {title}
              {billAlias ? `(${billAlias.alias})` : ''}
            </Link>
          );
        },
      },
      {
        columnId: DefaultBillTableColumns.BILL_TITLE,
        name: 'Bill Title',
        selector: 'name',
        sortable: true,
        minWidth: '200px',
        sortKey: SortBy.BILL_TITLE,
        style: {
          padding: '0px 10px',
        },
        cell: (bill: Bill) => bill.name,
      },
      {
        columnId: DefaultBillTableColumns.BILL_SPONSORS,
        name: 'Bill Sponsors',
        selector: 'sponsors',
        sortable: false,
        minWidth: '150px',
        maxWidth: '300px',
        style: { position: 'relative', zIndex: 2 },
        cell: (bill: Bill) => renderBillSponsors(bill),
      },
      {
        columnId: DefaultBillTableColumns.DEMOCRATS,
        name: '(D)',
        selector: 'democrats',
        sortable: false,
        minWidth: '50px',
        maxWidth: '75px',
        style: {
          paddingLeft: '8px',
          paddingRight: '8px',
        },
        cell: (bill: Bill) => {
          const { sponsors } = bill;
          if (!sponsors) return undefined;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === democratPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        columnId: DefaultBillTableColumns.REPUBLICANS,
        name: '(R)',
        selector: 'republican',
        sortable: false,
        minWidth: '50px',
        maxWidth: '75px',
        style: {
          paddingLeft: '8px',
          paddingRight: '8px',
        },
        cell: (bill: Bill) => {
          const { sponsors } = bill;
          if (!sponsors) return undefined;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === republicanPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        columnId: DefaultBillTableColumns.BILL_STATUS,
        name: 'Bill Status',
        selector: 'status',
        sortable: true,
        ignoreRowClick: true,
        button: true,
        minWidth: '130px',
        maxWidth: '130px',
        sortKey: SortBy.BILL_STATUS,
        cell: (bill: Bill) => (
          <div>
            <BillStatusBadge bill={bill} isCompactMode />
          </div>
        ),
      },
      {
        columnId: DefaultBillTableColumns.SESSION,
        name: 'Session',
        selector: 'session.displayName',
        sortable: true,
        maxWidth: '150px',
        wrap: true,
        sortKey: SortBy.BILL_SESSION,
      },
      {
        columnId: DefaultBillTableColumns.LATEST_ACTION,
        name: 'Latest Action',
        selector: 'action_description',
        sortable: false,
        maxWidth: '150px',
        cell: (bill: BillSummary) => getMostRecentAction(bill)?.description,
      },
      {
        columnId: DefaultBillTableColumns.ACTION_DATE,
        name: 'Action Date',
        selector: 'action_date',
        sortable: true,
        minWidth: '115px',
        maxWidth: '150px',
        sortKey: SortBy.RECENT_ACTIVITY,
        cell: (bill: BillSummary) => {
          const recentAction = getMostRecentAction(bill);
          return recentAction ? formatDate(recentAction.actionDate, 'short') : '';
        },
      },
      {
        columnId: DefaultBillTableColumns.UPCOMING_HEARING,
        name: 'Upcoming Hearing',
        selector: 'hearings',
        maxWidth: '300px',
        sortable: true,
        sortKey: SortBy.UPCOMING_HEARINGS,
        wrap: true,
        cell: (bill: BillSummary) => renderUpcomingHearings(bill),
      },
    ];

    const latestKeyActionColumn: ColumnWithId = {
      columnId: DefaultBillTableColumns.LATEST_KEY_ACTION,
      name: 'Latest Key Action',
      selector: 'key-action',
      maxWidth: '200px',
      sortKey: SortBy.RECENT_ACTIVITY,
      cell: (bill: BillSummary) => getMostRecentKeyAction(bill),
    };

    if (isIllinoisSearch) {
      baseColumns.splice(12, 0, latestKeyActionColumn);
    }

    // Custom fields
    const customColumns: ColumnWithId[] = orgId
      ? customFields.map((field) => ({
          columnId: field.title,
          name: field.title,
          selector: field.title,
          maxWidth: '200px',
          minWidth: '150px',
          cell: (bill: BillSummary) => (
            <BillCustomFieldValue bill={bill} field={field} />
          ),
        }))
      : [];

    const columnMap = [...baseColumns, ...customColumns].reduce<
      Record<string, SortableDataTableColumn>
    >((acc, col) => {
      acc[col.columnId] = col;
      return acc;
    }, {});

    return selectedColumns
      .map((id) => columnMap[id])
      .filter((col): col is SortableDataTableColumn => Boolean(col));
  }, [
    isIllinoisSearch,
    customFields,
    selectedColumns,
    democratPartyIdentifier,
    republicanPartyIdentifier,
    teamModeId,
  ]);

  const getDefaultSort = useCallback((): {
    selector: string;
    direction: 'asc' | 'desc';
  } => {
    const sorderOrderKey = sort?.sortBy ?? SortBy.RECENT_ACTIVITY;
    const selector =
      columns
        .find((column) => column.sortKey === sorderOrderKey)
        ?.selector?.toString() ?? 'action_date';
    return { selector, direction: sort?.isReversed ? 'desc' : 'asc' };
  }, [sort, columns]);

  return (
    <>
      <div className="list results-pane">
        <div className="result-count">
          <div className="result-total">
            <div className="result-dropdown-container">
              <div className="mb-2">{results?.count ?? 0} Results</div>
              <BillBulkActionsDropdown bills={selectedRows} />
            </div>
          </div>

          <div className="view-dropdown">
            {orgId && (
              <ColumnSelectDropdown
                columnOptions={columnOptions}
                selectedColumns={selectedColumns}
                setSelectedColumns={handleColumnChange}
              />
            )}
            <BillViewDropdown />
          </div>
        </div>
        {loadingPage && (
          <div className="table-spinner-overlay">
            <Spinner animation="border" role="status"></Spinner>
          </div>
        )}
        {showTableResults && (
          <div className="table bill-table" id="bill-search-results-table">
            <ReactTooltip
              anchorId={tooltipAnchor}
              className="tooltip"
              clickable
              delayHide={100}
              isOpen={showTooltip}
              place="bottom"
              setIsOpen={setShowTooltip}
            >
              {tipContent}
            </ReactTooltip>
            <DataTable
              columns={columns}
              customStyles={{
                cells: {
                  style: {
                    zIndex: 1,
                    position: 'relative',
                  },
                },
              }}
              data={results.data}
              defaultSortAsc={getDefaultSort().direction === 'asc'}
              defaultSortField={getDefaultSort().selector}
              disabled={loadingPage}
              noContextMenu
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePageSizeChange}
              onSelectedRowsChange={handleSelectedRowsChange}
              onSort={handleSortChange}
              pagination
              paginationDefaultPage={query.page}
              paginationPerPage={query.pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationServer
              paginationServerOptions={{
                persistSelectedOnSort: true,
              }}
              paginationTotalRows={results.count}
              responsive
              selectableRowSelected={handleSelectableRowsSelected}
              selectableRows
              selectableRowsVisibleOnly
              sortIcon={<FontAwesomeIcon icon={faCaretDown} />}
              sortServer={true}
            />
            {query.isAISearch && !!results.data.length && (
              <FeedbackPrompt
                feedbackType={FeedbackType.VersionToVersionSummaryInaccurate}
                message={
                  <span className="feedback-header-text">
                    {t('feedback.aiSearchHeaderText')}
                  </span>
                }
                setFeedbackState={handleSearchFeedback}
                submitFeedbackMetadata={{
                  count: results.count,
                  searchId,
                }}
              />
            )}
          </div>
        )}

        {!loadingPage && (!results || results?.count == 0) && (
          <div className="container">
            <NoBillResults />
          </div>
        )}
      </div>
    </>
  );
};

export default TabularBillSearchResults;
